import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import { collection, getDocs, query, where, doc, getDoc, writeBatch, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';

const SubjectScores = () => {
  const { user, userData } = useUserAuth();
  const { classId, subjectId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [subjectInfo, setSubjectInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editableScores, setEditableScores] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [isSaveLocked, setIsSaveLocked] = useState(false);
  const [lockLoading, setLockLoading] = useState(true);

  const getGrade = (total) => {
    if (total >= 70) return 'A';
    if (total >= 60) return 'B';
    if (total >= 50) return 'C';
    if (total >= 45) return 'D';
    if (total >= 40) return 'E';
    return 'F';
  };

  const validateScore = (value, field) => {
    // Allow empty string or special character 'A' for absent
    if (value === '' || value === 'A' || value === 'a') return true;
    
    const numValue = Number(value);
    if (isNaN(numValue)) return false;
    
    if (field === 'exam') {
      return numValue >= 0 && numValue <= 60;
    } else { // For CA fields
      return numValue >= 0 && numValue <= 20;
    }
  };

  const parseScoreValue = (value) => {
    if (value === 'A' || value === 'a') {
      return { numericValue: 0, isAbsent: true, displayValue: 'A' };
    }
    
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return { numericValue: 0, isAbsent: false, displayValue: '' };
    }
    
    return { numericValue: numValue, isAbsent: false, displayValue: value };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const classDoc = await getDoc(doc(db, 'classes', classId));
        setClassInfo(classDoc.data());

        const subjectDoc = await getDoc(doc(db, 'subjects', subjectId));
        setSubjectInfo(subjectDoc.data());

        const studentIds = classDoc.data().students || [];

        let studentsSnapshotDocs = [];
        for (let i = 0; i < studentIds.length; i += 30) {
          const chunk = studentIds.slice(i, i + 30);
          const studentsQuery = query(
            collection(db, 'students'),
            where('__name__', 'in', chunk)
          );
          const chunkSnapshot = await getDocs(studentsQuery);
          studentsSnapshotDocs.push(...chunkSnapshot.docs);
        }

        const initialEditableScores = {};
        const studentsData = studentsSnapshotDocs.map(doc => {
          const studentData = doc.data();
          const enrollment = studentData.enrollments?.find(e => e.classId === classId);
          const subject = enrollment?.subjects?.find(s => s.subjectId === subjectId);

          const firstCA = subject?.scores?.firstca ?? 0;
          const secondCA = subject?.scores?.secondca ?? 0;
          const exam = subject?.scores?.exam ?? 0;

          // Check if student was absent
          const wasFirstCAAbsent = subject?.scores?.firstcaAbsent ?? false;
          const wasSecondCAAbsent = subject?.scores?.secondcaAbsent ?? false;
          const wasExamAbsent = subject?.scores?.examAbsent ?? false;

          const total = firstCA + secondCA + exam;
          const grade = getGrade(total);

          // Store initial editable scores - use 'A' for absent, number for scores
          initialEditableScores[doc.id] = {
            firstCA: wasFirstCAAbsent ? 'A' : (firstCA === 0 ? '' : firstCA.toString()),
            secondCA: wasSecondCAAbsent ? 'A' : (secondCA === 0 ? '' : secondCA.toString()),
            exam: wasExamAbsent ? 'A' : (exam === 0 ? '' : exam.toString())
          };

          return {
            id: doc.id,
            ...studentData,
            scores: {
              firstCA,
              secondCA,
              exam,
              firstcaAbsent: wasFirstCAAbsent,
              secondcaAbsent: wasSecondCAAbsent,
              examAbsent: wasExamAbsent
            },
            total,
            grade
          };
        });

        setEditableScores(initialEditableScores);
        studentsData.sort((a, b) => b.total - a.total);
        const studentsWithPosition = studentsData.map((student, index) => ({
          ...student,
          position: index + 1
        }));

        setStudents(studentsWithPosition);
      } catch (error) {
        setError("Error fetching data: " + error.message);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classId, subjectId]);  

  useEffect(() => {
    const lockDocRef = doc(db, 'app-settings', 'lockScores');
    const unsubscribe = onSnapshot(lockDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setIsSaveLocked(docSnap.data().isLocked || false);
      } else {
        setIsSaveLocked(false);
      }
      setLockLoading(false);
    }, (error) => {
      console.error('Error listening to lock:', error);
      setIsSaveLocked(false);
      setLockLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const toggleSaveLock = async () => {
    if (lockLoading) return;

    setLockLoading(true);
    try {
      const lockDocRef = doc(db, 'app-settings', 'lockScores');
      
      // This creates the doc if missing, or updates if it exists
      await setDoc(lockDocRef, { isLocked: !isSaveLocked }, { merge: true });
      
      // Real-time listener will instantly update the UI
    } catch (error) {
      console.error('Error toggling lock:', error);
      setError('Failed to toggle save lock. Please try again.');
      setShowModal(true);
    } finally {
      setLockLoading(false);
    }
  };

  const handleScoreChange = (studentId, field, value) => {
    // Convert to uppercase if it's 'a' for consistency
    if (value === 'a') {
      value = 'A';
    }

    // Validate the input
    if (!validateScore(value, field)) return;

    setEditableScores(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
    setIsDirty(true);
  };

  const calculateTotal = (studentId) => {
    const scores = editableScores[studentId] || {};
    
    const firstCA = parseScoreValue(scores.firstCA).numericValue;
    const secondCA = parseScoreValue(scores.secondCA).numericValue;
    const exam = parseScoreValue(scores.exam).numericValue;
    
    return firstCA + secondCA + exam;
  };

  const getInputStyle = (value) => {
    if (value === 'A') {
      return {
        width: '50px', 
        height: '40px', 
        textAlign: 'center', 
        padding: '2px',
        backgroundColor: '#ffebee',
        color: '#d32f2f',
        fontWeight: 'bold',
      };
    }
    return {
      width: '50px', 
      height: '40px', 
      textAlign: 'center', 
      padding: '2px'
    };
  };

  const saveAllScores = async () => {
    // Global lock check - blocks all Teachers/Cashiers if locked
    if (isSaveLocked && ["Teacher", "Cashier"].includes(userData?.status)) {
      setError("You can't grade students right now. Please contact the admin to enable scoring.");
      setShowModal(true);
      return;
    }

    if (!isDirty) return;

    setSaving(true);
    try {
      const batch = writeBatch(db);

      students.forEach(student => {
        const studentRef = doc(db, 'students', student.id);
        const enrollments = student.enrollments || [];
        const updatedEnrollments = enrollments.map(enrollment => {
          if (enrollment.classId === classId) {
            const updatedSubjects = enrollment.subjects?.map(subject => {
              if (subject.subjectId === subjectId) {
                const firstCAParsed = parseScoreValue(editableScores[student.id]?.firstCA);
                const secondCAParsed = parseScoreValue(editableScores[student.id]?.secondCA);
                const examParsed = parseScoreValue(editableScores[student.id]?.exam);

                return {
                  ...subject,
                  scores: {
                    ...(subject.scores || {}),
                    firstca: firstCAParsed.numericValue,
                    secondca: secondCAParsed.numericValue,
                    exam: examParsed.numericValue,
                    firstcaAbsent: firstCAParsed.isAbsent,
                    secondcaAbsent: secondCAParsed.isAbsent,
                    examAbsent: examParsed.isAbsent
                  }
                };
              }
              return subject;
            });
            return {
              ...enrollment,
              subjects: updatedSubjects
            };
          }
          return enrollment;
        });
        batch.update(studentRef, { enrollments: updatedEnrollments });
      });

      await batch.commit();

      const updatedStudents = students.map(student => {
        const firstCAParsed = parseScoreValue(editableScores[student.id]?.firstCA);
        const secondCAParsed = parseScoreValue(editableScores[student.id]?.secondCA);
        const examParsed = parseScoreValue(editableScores[student.id]?.exam);
        
        const firstCA = firstCAParsed.numericValue;
        const secondCA = secondCAParsed.numericValue;
        const exam = examParsed.numericValue;
        const total = firstCA + secondCA + exam;
        const grade = getGrade(total);
        
        return {
          ...student,
          scores: { 
            firstCA, 
            secondCA, 
            exam,
            firstcaAbsent: firstCAParsed.isAbsent,
            secondcaAbsent: secondCAParsed.isAbsent,
            examAbsent: examParsed.isAbsent
          },
          total,
          grade
        };
      }).sort((a, b) => b.total - a.total).map((student, index) => ({
        ...student,
        position: index + 1
      }));

      setStudents(updatedStudents);
      setIsDirty(false);
      setSuccessMessage("Scores saved successfully!");
      setShowModal(true);
    } catch (error) {
      setError("Failed to save scores. Please try again.");
      setShowModal(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  return (
    <>
      <div className="scroll-fixed" style={{minHeight: '100vh'}}>
        <div className="top-elements row align-center space-between">
          <Link to="/home" className="back-link">
            <i className="fa-solid fa-arrow-left size3"></i>
          </Link>
          <div className="column align-center justify-center">
            <img src={Logo} className="middle-logo" alt="logo" />
          </div>
          <Link to="#" className="home-link" onClick={() => window.location.reload()}>
            <i className="fa-solid fa-rotate-right size4"></i>
          </Link>
        </div>
        <div style={{ maxWidth: '100vw', padding: '20px', marginTop: '80px' }}>
          <p className="caps male-female-stat">Student Scores for {subjectInfo?.name}</p>
          <p className="bold male-female-stat">{classInfo?.name} ({searchParams.get('sessionName')} - {searchParams.get('termName')} Term)</p>      
         
          <div className="scroll-fixed scroll-fixed1" style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ 
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem'
            }}
            className='male-female-stat'
            >
              <thead>
                <tr style={{ backgroundColor: '#f2f2f2' }}>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '40px' }}>S/N</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'left', minWidth: '120px' }}>Student</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>1st CA</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>2nd CA</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>Exam</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>Total</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>Grade</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const scores = editableScores[student.id] || {};
                  const total = calculateTotal(student.id);
                  const grade = getGrade(total);
                  
                  return (
                    <tr className='male-female-stat' key={student.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '4px', textAlign: 'center' }}>{student.position}</td>
                      <td style={{ padding: '4px' }}>{student.firstName} {student.lastName} {student.middleName}</td>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <input
                          className="score-input male-female-stat"
                          type="text"
                          value={scores.firstCA ?? ''}
                          onChange={(e) => handleScoreChange(student.id, 'firstCA', e.target.value)}
                          style={getInputStyle(scores.firstCA)}
                          placeholder="0"
                          maxLength={3}
                        />
                      </td>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <input
                          className="score-input male-female-stat"
                          type="text"
                          value={scores.secondCA ?? ''}
                          onChange={(e) => handleScoreChange(student.id, 'secondCA', e.target.value)}
                          style={getInputStyle(scores.secondCA)}
                          placeholder="0"
                          maxLength={3}
                        />
                      </td>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <input
                          className="score-input male-female-stat"
                          type="text"
                          value={scores.exam ?? ''}
                          onChange={(e) => handleScoreChange(student.id, 'exam', e.target.value)}
                          style={getInputStyle(scores.exam)}
                          placeholder="0"
                          maxLength={3}
                        />
                      </td>
                      <td style={{ padding: '4px', height: '40px', textAlign: 'center', fontWeight: 'bold' }}>
                        {total}
                      </td>
                      <td style={{ 
                        padding: '4px', 
                        textAlign: 'center', 
                        fontWeight: 'bold',
                        color: grade === 'F' ? 'red' : 'inherit'
                      }}>
                        {grade}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>                  
        </div>    

          <div style={{ marginTop: '-10px', marginLeft: '20px', textAlign: 'left' }}>
            {isSaveLocked && ["Teacher", "Cashier"].includes(userData?.status) ? (
              <div style={{ 
                padding: '8px 16px', 
                backgroundColor: '#ffebee', 
                color: '#d32f2f', 
                border: '1px solid #ffcdd2', 
                borderRadius: '4px', 
                fontSize: '0.9rem',
                display: 'inline-block',
                width: '90%',
              }}>
                <i className="fa-solid fa-lock" style={{ marginRight: '8px' }}></i>
                You can't grade students right now. Please contact the admin.
              </div>
            ) : (
              <>
                <button
                  onClick={saveAllScores}
                  disabled={saving || lockLoading}
                  className='male-female-stat'
                  style={{
                    padding: '8px 16px',
                    backgroundColor: (isDirty && !saving && !lockLoading) ? 'rgb(224, 193, 107)' : '#cccccc',
                    color: (isDirty && !saving && !lockLoading) ? '#111d2b' : '#111d2bad',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (isDirty && !saving && !lockLoading) ? 'pointer' : 'not-allowed'
                  }}
                >
                  {saving ? 'Saving Scores...' : 'Save All Scores'}
                </button>
                {isDirty && (
                  <span className="size5 male-female-stat" style={{ marginLeft: '10px', color: '#666' }}>
                    You have unsaved changes
                  </span>
                )}
              </>
            )}
          </div>

          {/* Lock Button - Only visible to Admin/Supreme (not Teacher or Cashier) */}
          {userData?.status && ["Admin", "Supreme"].includes(userData.status) && (
            <button
              onClick={toggleSaveLock}
              disabled={lockLoading}
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: isSaveLocked ? '#fc796f' : '#e0c16b',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
                cursor: lockLoading ? 'not-allowed' : 'pointer',
                zIndex: 1000,
                fontSize: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                opacity: lockLoading ? 0.7 : 1,
              }}
              title={isSaveLocked 
                ? "Save button is LOCKED for Teachers/Cashiers (Click to unlock)" 
                : "Click to LOCK Save button for Teachers/Cashiers"}
            >
              <i className={`fa-solid font-dark-blue ${isSaveLocked ? 'fa-lock' : 'fa-lock-open'}`}></i>
            </button>
          )}
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}
    </>
  );
};

export default SubjectScores;